import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useStrategiesStore } from "@/stores/strategiesStore";
import { api } from "@/lib/api";
import {
  MainSystemPrompt,
  StrategyWithVersions,
  StrategyVersion,
} from "@/lib/types";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const formSchema = z.object({
  callDescription: z.string().min(10, {
    message: "Call description must be at least 10 characters.",
  }),
  callDuration: z.string().regex(/^\d+$/, {
    message: "Call duration must be a number.",
  }),
});

type FormValues = z.infer<typeof formSchema>;

const TestGenPage: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [mainSystemPrompts, setMainSystemPrompts] = useState<
    MainSystemPrompt[]
  >([]);
  const { strategiesByOrg } = useStrategiesStore();
  const [selectedStrategy, setSelectedStrategy] = useState<string | null>(null);
  const [selectedMainPrompt, setSelectedMainPrompt] = useState<string | null>(
    null
  );
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      callDescription: "",
      callDuration: "",
    },
  });

  const onSubmit = (values: FormValues) => {
    console.log(values);
    generatePrompts(values);
  };

  const generatePrompts = (data: FormValues) => {
    // Stub function for generating prompts
    console.log("Generating prompts with data:", data);
    // Implement your logic here
  };

  const generateTests = () => {
    if (selectedMainPrompt && selectedVersion) {
      // Stub function for generating tests
      console.log("Generating tests with:", {
        selectedMainPrompt,
        selectedVersion,
      });
      // Implement your logic here
    } else {
      console.error(
        "Please select both a main system prompt and a strategy version"
      );
    }
  };

  const handleOpenDialog = async () => {
    try {
      const prompts = await api.getAllMainSystemPrompts();
      setMainSystemPrompts(prompts);
      setOpenDialog(true);
    } catch (error) {
      console.error("Failed to fetch main system prompts:", error);
    }
  };

  const getStrategiesArray = (): StrategyWithVersions[] => {
    return Object.values(strategiesByOrg).flat();
  };

  const getSelectedStrategyVersions = (): StrategyVersion[] => {
    if (!selectedStrategy) return [];
    const strategy = getStrategiesArray().find(
      (s) => s.id === selectedStrategy
    );
    return strategy ? strategy.versions : [];
  };

  return (
    <FormProvider {...form}>
      <div className="space-y-8">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="callDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Call Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe the phone call you want to make..."
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Provide details about what needs to be covered on the call.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="callDuration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Approximate Call Duration (minutes)</FormLabel>
                  <FormControl>
                    <Input placeholder="30" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter the estimated duration of the call in minutes.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit">Figure out prompts</Button>
          </form>
        </Form>

        <Dialog open={openDialog} onOpenChange={setOpenDialog}>
          <DialogTrigger asChild>
            <Button onClick={handleOpenDialog}>
              Test with existing prompts
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Test with Existing Prompts</DialogTitle>
              <DialogDescription>
                Select a main system prompt, strategy, and strategy version to
                test.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
                <FormLabel htmlFor="mainPrompt" className="sm:text-right">
                  Main Prompt
                </FormLabel>
                <Select onValueChange={setSelectedMainPrompt}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select a main system prompt" />
                  </SelectTrigger>
                  <SelectContent>
                    {mainSystemPrompts.map((prompt) => (
                      <SelectItem key={prompt.id} value={prompt.id}>
                        {prompt.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
                <FormLabel htmlFor="strategy" className="sm:text-right">
                  Strategy
                </FormLabel>
                <Select onValueChange={setSelectedStrategy}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select a strategy" />
                  </SelectTrigger>
                  <SelectContent>
                    {getStrategiesArray().map((strategy) => (
                      <SelectItem key={strategy.id} value={strategy.id}>
                        {strategy.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
                <FormLabel htmlFor="strategyVersion" className="sm:text-right">
                  Version
                </FormLabel>
                <Select onValueChange={setSelectedVersion}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select a strategy version" />
                  </SelectTrigger>
                  <SelectContent>
                    {getSelectedStrategyVersions().map((version) => (
                      <SelectItem key={version.id} value={version.id}>
                        {version.version_description || `Version ${version.id}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={generateTests}>Test</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </FormProvider>
  );
};

export default TestGenPage;
